import React, { useCallback, useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash';
import {
    Select,
    SelectStyles,
    Icon,
    Block
} from "../../../../seenspire-library";
import { getToken } from "../../../backend/session";
import { api } from "../../../backend/api";
import {
    CustomChannelSourceTypeEnum as TypesEnum,
} from "../../../interfaces";

const getHeaders = () => {
    const currentToken = getToken();
  
    const headers = {
      Authorization: `Bearer ${currentToken.access_token}`,
    };
  
    return headers;
};

interface PowerBiDashboardFieldsProps {
  connection: any;
  selectedType: any;
  singleInputCustomStyles: any;
  onValidationChange: (isValid: boolean) => void;
  onFieldsChange: (fields: {
    workspace_id: string | null;
    dashboard_id: string | null;
    dashboard_name?: string;
    orientation?: string;
    rendertype?: "live" | "image";
  }) => void;
  onError: (error: { error: boolean; message: string }) => void;
  onValidatingChange: (isValidating: boolean) => void;
}

interface Workspace {
  id: string;
  title: string;
}

interface Dashboard {
  id: string;
  title: string;
}

export const PowerBiDashboardFields: React.FC<PowerBiDashboardFieldsProps> = ({
  connection,
  onValidationChange,
  onFieldsChange,
  onError,
  onValidatingChange,
}) => {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(null);
  const [selectedDashboard, setSelectedDashboard] = useState<Dashboard | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(false);
  const [isLoadingDashboards, setIsLoadingDashboards] = useState(false);
  const [rendertype, setRendertype] = useState<"live" | "image">("image");

  // Fetch workspaces when component mounts or connection changes
  useEffect(() => {
    if (!connection?.id) return;
    
    const fetchWorkspaces = async () => {
      setIsLoadingWorkspaces(true);
      setIsValidating(true);
      onValidatingChange(true);
      
      try {
        const response = await api.get(
          `${process.env.REACT_APP_API}/api/networkconnections/${connection.id}/customoptions/${TypesEnum.POWERBIDASHBOARD}/options/workspace_id`,
          {
            method: "GET",
            headers: getHeaders()
          }
        );

        const workspaceOptions = response.data
          .map((workspace: any) => ({
            id: workspace.id,
            title: workspace.title
          }));

        setWorkspaces(workspaceOptions);
        onError({ error: false, message: "" });
      } catch (error: any) {
        onError({
          error: true,
          message: error.response?.data?.message || "Failed to fetch workspaces. Please try again."
        });
        onValidationChange(false);
      } finally {
        setIsLoadingWorkspaces(false);
        setIsValidating(false);
        onValidatingChange(false);
      }
    };

    fetchWorkspaces();
  }, [connection?.id]);

  // Fetch dashboards when workspace is selected
  useEffect(() => {
    if (!connection?.id || !selectedWorkspace?.id) {
      setDashboards([]);
      return;
    }
    
    const fetchDashboards = async () => {
      setIsLoadingDashboards(true);
      setIsValidating(true);
      onValidatingChange(true);
      
      try {
        const response = await api.get(
          `${process.env.REACT_APP_API}/api/networkconnections/${connection.id}/customoptions/${TypesEnum.POWERBIDASHBOARD}/options/dashboard_id`,
          {
            method: "GET",
            headers: getHeaders(),
            params: {
              workspace_id: selectedWorkspace.id
            }
          }
        );

        const dashboardOptions = response.data.map((dashboard: any) => ({
          id: dashboard.id,
          title: dashboard.title
        }));

        setDashboards(dashboardOptions);
        onError({ error: false, message: "" });
      } catch (error: any) {
        onError({
          error: true,
          message: error.response?.data?.message || "Failed to fetch dashboards. Please try again."
        });
        onValidationChange(false);
        setDashboards([]);
      } finally {
        setIsLoadingDashboards(false);
        setIsValidating(false);
        onValidatingChange(false);
      }
    };

    fetchDashboards();
  }, [connection?.id, selectedWorkspace]);

  const handleWorkspaceChange = (selected: any) => {
    const workspace = workspaces.find(w => w.id === selected.value);
    setSelectedWorkspace(workspace || null);
    setSelectedDashboard(null);
    
    const fields = {
      workspace_id: selected.value,
      dashboard_id: null,
      dashboard_name: undefined,
      orientation: "landscape"
    };
    onFieldsChange(fields);
    // Reset validation when workspace changes
    onValidationChange(false);
  };

  const handleDashboardChange = (selected: any) => {
    const dashboard = dashboards.find(d => d.id === selected.value);
    setSelectedDashboard(dashboard || null);
    
    if (selectedWorkspace && dashboard) {
      const fields = {
        workspace_id: selectedWorkspace.id,
        dashboard_id: dashboard.id,
        dashboard_name: dashboard.title,
        orientation: "landscape"
      };
      onFieldsChange(fields);
      // Ensure validation is set to true when both workspace and dashboard are selected
      onValidationChange(true);
    }
  };

  const handleRendertypeChange = (selected: any) => {
    setRendertype(selected.value);
    onFieldsChange({
      workspace_id: selectedWorkspace ? selectedWorkspace.id : null,
      dashboard_id: selectedDashboard ? selectedDashboard.id : null,
      dashboard_name: selectedDashboard ? selectedDashboard.title : undefined,
      orientation: "landscape",
      rendertype: selected.value,
    });
  };

  const LoadingIndicator = () => (
    <Block display="flex" alignItems="center" padding={{ right: 8 }}>
      <Icon name="loading" iconSize="small" />
    </Block>
  );

  return (
    <>
      <Select
        title="Workspace Name"
        value={selectedWorkspace ? { 
          value: selectedWorkspace.id,
          label: selectedWorkspace.title
        } : undefined}
        options={workspaces.map(workspace => ({
          value: workspace.id,
          label: workspace.title
        }))}
        onChange={handleWorkspaceChange}
        isDisabled={isValidating}
        isLoading={isLoadingWorkspaces}
        styles={{
          ...SelectStyles,
          loadingIndicator: (base: any) => ({
            ...base,
            display: 'flex',
            alignItems: 'center',
            padding: '0 8px'
          })
        }}
        placeholder={isLoadingWorkspaces ? "Loading workspaces..." : "Select a workspace"}
      />
      <Select
        title="Dashboard Name"
        value={selectedDashboard ? {
          value: selectedDashboard.id,
          label: selectedDashboard.title
        } : undefined}
        options={dashboards.map(dashboard => ({
          value: dashboard.id,
          label: dashboard.title
        }))}
        onChange={handleDashboardChange}
        isDisabled={!selectedWorkspace || isValidating}
        isLoading={isLoadingDashboards}
        styles={{
          ...SelectStyles,
          loadingIndicator: (base: any) => ({
            ...base,
            display: 'flex',
            alignItems: 'center',
            padding: '0 8px'
          })
        }}
        placeholder={
          isLoadingDashboards
            ? "Loading dashboards..."
            : selectedWorkspace
            ? "Select a dashboard"
            : "Select a workspace first"
        }
      />
      <Select
        title="Display method"
        value={{
          value: rendertype,
          label: rendertype === "live" ? "Live view" : "Screenshot",
        }}
        options={[
          { value: "image", label: "Screenshot" },
          { value: "live", label: "Live view" },
        ]}
        onChange={handleRendertypeChange}
        styles={SelectStyles}
        placeholder="Select display method"
      />
    </>
  );
}; 